import { useSyncExternalStore } from 'react';

/**
 * Ширина экрана для раскладки ПО (решение Ромы 05.10.2026, erp-mobile/ПЕРЕДАЧА-В-БОЙ.md):
 * телефон ≤ 767 px · планшет 768–1100 px · дальше — компьютер как был.
 * Признак дублируется на <html data-vp="phone|tablet"> — на него опирается слой styles/mobile.css
 * (у компьютера атрибута нет, слой его не касается).
 */
export type Viewport = 'phone' | 'tablet' | 'desktop';

export const PHONE_MAX = 767;
export const TABLET_MAX = 1100;

function read(): Viewport {
  // matchMedia нет в окружении тестов — там всегда компьютер. Ширину берём у окна (то же число, что видят медиазапросы).
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function' || import.meta.env.MODE === 'test') return 'desktop';
  const w = window.innerWidth;
  if (w <= PHONE_MAX) return 'phone';
  if (w <= TABLET_MAX) return 'tablet';
  return 'desktop';
}

let current: Viewport = read();
const listeners = new Set<() => void>();

function apply(): void {
  if (typeof document === 'undefined') return;
  if (current === 'desktop') document.documentElement.removeAttribute('data-vp');
  else document.documentElement.setAttribute('data-vp', current);
}

function onChange(): void {
  const next = read();
  if (next === current) return;
  current = next;
  apply();
  listeners.forEach((f) => f());
}

if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  apply();
  for (const q of [`(max-width: ${PHONE_MAX}px)`, `(max-width: ${TABLET_MAX}px)`]) {
    const mq = window.matchMedia(q);
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else if (typeof (mq as MediaQueryList).addListener === 'function') (mq as MediaQueryList).addListener(onChange); // старые Safari
  }
  // Поворот экрана и изменение окна: событие медиазапроса приходит не во всех браузерах — сверяемся и по resize.
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
}

function subscribe(f: () => void): () => void { listeners.add(f); return () => { listeners.delete(f); }; }

export function getViewport(): Viewport { return current; }
export function useViewport(): Viewport { return useSyncExternalStore(subscribe, getViewport, () => 'desktop' as Viewport); }
/** Телефон или планшет — мобильный каркас (нижнее меню / рейка, новая шапка). */
export const isMobile = (v: Viewport): boolean => v !== 'desktop';

// iPhone/iPad: Safari приближает страницу при фокусе в поле со шрифтом меньше 16 px (на телефоне в «Новом заказе» и окне «Позиция»
// поля 14 px — решение Ромы 01.11.2026). maximum-scale=1 это автоприближение отключает, а щипок пользователя Safari с iOS 10
// всё равно разрешает. Android не трогаем: там Chrome по такой записи запретил бы и щипок.
if (typeof navigator !== 'undefined' && typeof document !== 'undefined') {
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]');
  if (ios && meta && !/maximum-scale/.test(meta.content)) meta.content += ', maximum-scale=1';
}
